import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { getCustomerDetails, clearCustomerDetails } from '../../../../redux/Slices/authSlice';

const CustomerDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { customerDetails, isLoading } = useSelector((s) => s.auth);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (id) {
      dispatch(getCustomerDetails(id));
    }
    return () => {
      dispatch(clearCustomerDetails());
    };
  }, [dispatch, id]);

  if (isLoading) {
    return (
      <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
        {t('customers.view.loading')}
      </div>
    );
  }

  if (!customerDetails) {
    return (
      <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: isRTL ? 'rtl' : 'ltr' }}>
        <button onClick={() => navigate('/customers')} style={{ background: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', marginBottom: '12px' }}>{t('customers.view.back')}</button>
        <p>{t('customers.view.noData')}</p>
      </div>
    );
  }

  const customer = customerDetails.data || customerDetails;

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{t('customers.view.title')} #{customer.id}</h1>
        <button onClick={() => navigate('/customers')} style={{ background: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer' }}>
          {t('customers.view.back')}
        </button>
      </div>

      <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '12px', overflow: 'hidden', textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #333', backgroundColor: '#1a1f2e' }}>
          <strong style={{ fontSize: '18px', color: 'white' }}>{t('customers.view.title')} #{customer.id}</strong>
        </div>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <InfoRow label={t('customers.view.id')} value={customer.id || '-'} isRTL={false} />
            <InfoRow label={t('customers.view.code')} value={customer.code || '-'} isRTL={isRTL} />
            {isRTL ? (
              <>
                <InfoRow label={t('customers.view.nameAr')} value={customer.name_ar || customer.name_en || customer.name || '-'} isRTL={true} />
                <InfoRow label={t('customers.view.nameEn')} value={customer.name_en || '-'} isRTL={false} />
              </>
            ) : (
              <>
                <InfoRow label={t('customers.view.nameEn')} value={customer.name_en || customer.name_ar || customer.name || '-'} isRTL={false} />
                <InfoRow label={t('customers.view.nameAr')} value={customer.name_ar || '-'} isRTL={false} valueDirection="rtl" />
              </>
            )}
            <InfoRow label={t('customers.view.contactPerson')} value={customer.contact_person || '-'} isRTL={isRTL} />
            <InfoRow label={t('customers.view.email')} value={customer.email || '-'} isRTL={false} />
            <InfoRow label={t('customers.view.phone')} value={customer.phone || '-'} isRTL={isRTL} />
            <InfoRow label={t('customers.view.taxNumber')} value={customer.tax_number || '-'} isRTL={isRTL} />
            <div>
              <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '8px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('customers.view.status')}</div>
              <span style={{
                backgroundColor: customer.is_active ? '#AC2000' : '#dc3545',
                color: 'white',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'inline-block'
              }}>
                {customer.is_active ? t('customers.view.active') : t('customers.view.inactive')}
              </span>
            </div>
            <div style={{ gridColumn: '1 / span 2' }}>
              <InfoRow label={t('customers.view.address')} value={customer.address || '-'} isRTL={isRTL} />
            </div>
            {customer.notes && (
              <div style={{ gridColumn: '1 / span 2' }}>
                <InfoRow label={t('customers.view.notes')} value={customer.notes} isRTL={isRTL} />
              </div>
            )}
            {customer.account && (
              <div style={{ gridColumn: '1 / span 2' }}>
                <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '8px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('customers.view.account')}</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  {customer.account.code} - {customer.account.name}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, isRTL = true, valueDirection = null }) => {
  const valueDir = valueDirection !== null ? valueDirection : (isRTL ? 'rtl' : 'ltr');
  // For alignment, use isRTL (label alignment), but for text direction use valueDirection if provided
  const valueAlign = isRTL ? 'right' : 'left';
  return (
    <div>
      <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '8px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{label}</div>
      <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'white', direction: valueDir, textAlign: valueAlign }}>{value}</div>
    </div>
  );
};

export default CustomerDetails;

